import {Avatar, Box, HStack, Icon, Text, VStack} from '@chakra-ui/react';
import {ChevronRightIcon} from '@chakra-ui/icons';
import {HaliRenderOne} from '@synako/haligator';
import {State} from '@synako/halitrek';
import {ConversationDesc} from 'api-types/ffconnect-hal';
import {formatDateForConversation} from 'ff-common';
import {useTranslation} from 'react-i18next';

/**
 * Ligne d'une conversation dans l'onglet "Toutes les conversations" de la popin (mode commercial).
 * Basée sur un ConversationDesc (vue périmètre) et non sur une participation : pas de compteur
 * de messages non lus, le libellé se rabat sur la raison sociale du client concerné.
 */
export const ConversationClientLineConversation: React.FC<{
  conversationState: State<ConversationDesc>;
  onClick?: () => void;
}> = ({conversationState, onClick}) => {
  const {t} = useTranslation();
  const conversation = conversationState.data;

  // Formatage de la date du dernier message
  const dateFormatee = formatDateForConversation(
    conversation.dernier_message_le,
    t('client.conversations.hier'),
  );

  // Rendu de la ligne, paramétré par le client résolu (ou non) via le lien HAL.
  const renderLigne = (titre: string, sousTitre: string) => (
    <Box
      mb={3}
      mt={3}
      borderRadius='8px'
      cursor='pointer'
      _hover={{bg: 'gray.bg'}}
      onClick={onClick}
      w='100%'
    >
      <HStack
        spacing={3}
        p={1}
        align='center'
        w='100%'
      >
        {/* Avatar du client concerné */}
        <Avatar
          name={sousTitre}
          color='white'
          size='sm'
          ml={1}
        />

        {/* Contenu */}
        <VStack
          align='flex-start'
          spacing={0}
          flex={1}
          minW={0}
        >
          <Text
            fontSize='sm'
            fontWeight='600'
            color='black.text'
            noOfLines={1}
          >
            {titre}
          </Text>
          <HStack
            spacing={2}
            mt={1}
          >
            {sousTitre && (
              <Text
                fontSize='xs'
                color='gray.dark'
                fontWeight='600'
                noOfLines={1}
              >
                {sousTitre}
              </Text>
            )}
            <Text
              fontSize='xs'
              color='gray.dark'
              noOfLines={1}
            >
              {dateFormatee}
            </Text>
          </HStack>
        </VStack>

        {/* Flèche à droite */}
        <Icon
          as={ChevronRightIcon}
          boxSize={5}
          color='gray.dark'
        />
      </HStack>
    </Box>
  );

  return (
    <HaliRenderOne
      nav={conversationState.follow('client')}
      // Si le client ne se résout pas, on affiche au moins le titre (ou le titre par défaut).
      skeleton={() =>
        renderLigne(
          conversation.titre ?? t('client.conversations.titre_defaut'),
          '',
        )
      }
      fallback={() =>
        renderLigne(
          conversation.titre ?? t('client.conversations.titre_defaut'),
          '',
        )
      }
      render={({data: client}) => {
        const raisonSociale = client.tolede.raison_sociale ?? '';
        const titre =
          conversation.titre ??
          t('back_office.conversations.conversation_avec', {
            raison_sociale: raisonSociale,
          });
        return renderLigne(titre, raisonSociale);
      }}
    />
  );
};
